/**
 * Platform Admin API Service
 * Handles all API calls for the platform admin console
 * Uses a dedicated axios instance without tenant interceptors
 */

import axios from 'axios';

// Create a dedicated axios instance for platform API
const platformAxios = axios.create({
  baseURL: '/content-management-api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token interceptor
platformAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('platform_token');
  const configToReturn = { ...config };
  if (token) {
    configToReturn.headers.Authorization = `Bearer ${token}`;
  }
  return configToReturn;
});

// Handle auth errors
platformAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear stored tokens and redirect to login
      localStorage.removeItem('platform_token');
      localStorage.removeItem('platform_refresh_token');
      localStorage.removeItem('platform_user');
      window.location.href = '/platform/login';
    }
    return Promise.reject(error);
  },
);

const PLATFORM_API_BASE = '/platform';

export const platformApi = {
  // Stats
  async getStats() {
    const response = await platformAxios.get(`${PLATFORM_API_BASE}/stats`);
    return response.data?.data;
  },

  // Tenants
  async getTenants(params = {}) {
    const response = await platformAxios.get(`${PLATFORM_API_BASE}/tenants`, { params });
    // Response format: { success: true, count: N, data: [...] }
    const { data } = response;
    if (Array.isArray(data?.data)) {
      return data.data;
    }
    return Array.isArray(data) ? data : [];
  },

  async getTenant(slug) {
    const response = await platformAxios.get(`${PLATFORM_API_BASE}/tenants/${slug}`);
    return response.data?.data;
  },

  async createTenant(tenantData) {
    const response = await platformAxios.post(`${PLATFORM_API_BASE}/tenants`, tenantData);
    return response.data?.data;
  },

  async updateTenant(slug, tenantData) {
    const response = await platformAxios.put(`${PLATFORM_API_BASE}/tenants/${slug}`, tenantData);
    return response.data?.data;
  },

  async getTenantConfig(slug) {
    const response = await platformAxios.get(`${PLATFORM_API_BASE}/tenants/${slug}/config`);
    return response.data?.data;
  },

  async updateTenantConfig(slug, configData) {
    const response = await platformAxios.patch(`${PLATFORM_API_BASE}/tenants/${slug}/config`, configData);
    return response.data;
  },

  async deleteTenant(slug) {
    const response = await platformAxios.delete(`${PLATFORM_API_BASE}/tenants/${slug}`);
    return response.data;
  },

  async suspendTenant(slug) {
    const response = await platformAxios.post(`${PLATFORM_API_BASE}/tenants/${slug}/suspend`);
    return response.data?.data;
  },

  async activateTenant(slug) {
    const response = await platformAxios.post(`${PLATFORM_API_BASE}/tenants/${slug}/activate`);
    return response.data?.data;
  },

  /** Tenant roles migration from legacy config lists (platform admin). */
  async migrateTenantRoles(slug, { dryRun = true, reason } = {}) {
    const response = await platformAxios.post(
      `${PLATFORM_API_BASE}/tenants/${slug}/migrate/roles`,
      { dryRun, ...(reason ? { reason } : {}) },
    );
    return response.data;
  },

  /** Features collection cleanup migration (platform admin). */
  async migrateTenantFeatures(slug, { dryRun = true, reason } = {}) {
    const response = await platformAxios.post(
      `${PLATFORM_API_BASE}/tenants/${slug}/migrate/features`,
      { dryRun, ...(reason ? { reason } : {}) },
    );
    return response.data;
  },

  /** Seed customer navigation/home layouts (platform admin). */
  async seedTenantCustomerLayouts(slug, { dryRun = true, reason } = {}) {
    const response = await platformAxios.post(
      `${PLATFORM_API_BASE}/tenants/${slug}/seed/customer-layouts`,
      { dryRun, ...(reason ? { reason } : {}) },
    );
    return response.data;
  },

  // Platform Users
  async getUsers() {
    const response = await platformAxios.get(`${PLATFORM_API_BASE}/users`);
    return response.data?.data;
  },

  async createUser(userData) {
    const response = await platformAxios.post(`${PLATFORM_API_BASE}/users`, userData);
    return response.data?.data;
  },

  async updateUser(userId, userData) {
    const response = await platformAxios.patch(`${PLATFORM_API_BASE}/users/${userId}`, userData);
    return response.data?.data;
  },

  async updateUserAssignments(userId, tenantSlugs) {
    const response = await platformAxios.put(`${PLATFORM_API_BASE}/users/${userId}/assignments`, { tenantSlugs });
    return response.data?.data;
  },

  async deactivateUser(userId) {
    return this.updateUser(userId, { isActive: false });
  },

  async activateUser(userId) {
    return this.updateUser(userId, { isActive: true });
  },

  // Audit Logs
  async getAuditLogs(params = {}) {
    const response = await platformAxios.get(`${PLATFORM_API_BASE}/audit`, { params });
    return response.data?.data;
  },

  // Settings
  async getSettings() {
    const response = await platformAxios.get(`${PLATFORM_API_BASE}/settings`);
    return response.data?.data;
  },

  async updateSettings(settings) {
    const response = await platformAxios.patch(`${PLATFORM_API_BASE}/settings`, settings);
    return response.data?.data;
  },

  // Health Check
  async checkHealth() {
    const response = await platformAxios.get(`${PLATFORM_API_BASE}/health`);
    return response.data?.data;
  },

  // ============================================
  // Tenant Import/Export
  // ============================================

  /**
   * Validate an import ZIP file before creating tenant
   * @param {FormData} formData - FormData containing the 'file' field
   * @returns {Promise<{valid: boolean, errors: string[], warnings: string[], stats?: object}>}
   */
  async validateImport(formData) {
    const response = await platformAxios.post(`${PLATFORM_API_BASE}/tenants/validate-import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Create tenant with import from ZIP
   * @param {FormData} formData - FormData containing file, slug, name, adminEmail, plan
   * @returns {Promise<object>}
   */
  async importTenant(formData) {
    const response = await platformAxios.post(`${PLATFORM_API_BASE}/tenants/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Export tenant data as ZIP file
   * @param {string} slug - Tenant slug
   * @returns {Promise<Blob>} - ZIP file blob for download
   */
  async exportTenant(slug) {
    const response = await platformAxios.get(`${PLATFORM_API_BASE}/tenants/${slug}/export`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Download tenant export (triggers browser download)
   * @param {string} slug - Tenant slug
   */
  async downloadTenantExport(slug) {
    const blob = await this.exportTenant(slug);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `tenant-export-${slug}-${new Date().toISOString().split('T')[0]}.zip`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};

export default platformApi;
